import React, { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gql, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { PRODUCT_FRAGMENT } from '../../component/productPreview/ProductPreview';
import { PRODUCT_LIST_ITEM_FRAGMENT } from '../../query/productFragments';
import { SCHEMA_CONTEXT, safeJsonLd, breadcrumbSchemaOrg } from '../../util/schemaOrg';
import { productModelUrl, brandUrl, webappURL, decodeRouteParam } from '../../util/url';
import { SORT_OPTIONS } from '../search/searchSort/SortOptions';

import Loading from '../../component/loading/Loading';
import Error from '../../component/error/Error';
import NotFound from '../notFound/NotFound';

import ProductModelHero from './components/ProductModelHero';
import ProductModelResults from './components/ProductModelResults';

import './ProductModel.css';

const PAGE_SIZE = 40;

const GET_CATALOG_MODEL_WITH_PRODUCTS = gql`
    query getCatalogModelWithProducts(
        $brandName: String!
        $modelName: String!
        $offset: Int!
        $limit: Int!
        $sort: String
    ) {
        getCatalogModelWithProducts(
            brandName: $brandName
            modelName: $modelName
            offset: $offset
            limit: $limit
            sort: $sort
        ) {
            model {
                id
                name
                name_kr
            }
            products {
                items {
                    ...ProductFragment
                    ...ProductListItemFragment
                    price
                }
                pagination {
                    total
                }
            }
        }
    }
    ${PRODUCT_FRAGMENT}
    ${PRODUCT_LIST_ITEM_FRAGMENT}
`;

/**
 * 브랜드 모델 상품 리스트 페이지.
 * URL 구조: /brand/:brandName/model/:modelName
 * fruitsfamily-web-server 의 getCatalogModelWithProducts API 를 사용합니다.
 * @param hostname
 */
export default function ProductModel({ hostname }) {
    const { brandName: rawBrandName, modelName: rawModelName } = useParams();

    const brandName = decodeRouteParam(rawBrandName);
    const modelName = decodeRouteParam(rawModelName);
    const [searchParams, setSearchParams] = useSearchParams();
    const sort = searchParams.get('sort') || SORT_OPTIONS.POPULAR.code;

    const handleSortChange = useCallback(
        (code) => {
            const next = new URLSearchParams(searchParams);
            if (code && code !== SORT_OPTIONS.POPULAR.code) {
                next.set('sort', code);
            } else {
                next.delete('sort');
            }
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const { loading, error, data, fetchMore } = useQuery(GET_CATALOG_MODEL_WITH_PRODUCTS, {
        variables: {
            brandName,
            modelName,
            offset: 0,
            limit: PAGE_SIZE,
            sort,
        },
        notifyOnNetworkStatusChange: true,
        skip: !brandName || !modelName,
    });

    const container = data && data.getCatalogModelWithProducts;
    const model = container && container.model;
    const items = (container && container.products && container.products.items) || [];
    const total = (container && container.products && container.products.pagination
        && container.products.pagination.total) || 0;

    const canonicalUrl = webappURL(hostname) + productModelUrl(brandName, modelName);

    const { metaTitle, metaDescription } = useMemo(() => {
        if (!model) {
            return { metaTitle: '', metaDescription: '' };
        }
        const modelLabel = model.name_kr ? `${model.name}(${model.name_kr})` : model.name;
        return {
            metaTitle: `${brandName} ${modelLabel} | 후루츠패밀리 - 빈티지 세컨핸드 패션 중고거래`,
            metaDescription: `${brandName} ${modelLabel} 중고 상품을 후루츠패밀리에서 모아보세요. 남성 · 여성 의류, 가방, 주얼리, 신발, 악세서리까지 안전하게 거래하세요.`,
        };
    }, [model, brandName]);

    const schemaOrgPreview = useMemo(() => {
        if (!model) return null;
        return {
            ...SCHEMA_CONTEXT,
            '@type': 'CollectionPage',
            name: metaTitle,
            description: metaDescription,
            url: canonicalUrl,
            about: {
                '@type': 'Brand',
                name: brandName,
                url: webappURL(hostname) + brandUrl(brandName),
            },
            inLanguage: 'ko-KR',
            isPartOf: {
                '@type': 'WebSite',
                name: '후루츠패밀리',
                url: webappURL(hostname),
            },
        };
    }, [model, metaTitle, metaDescription, canonicalUrl, brandName, hostname]);

    // 최초 로딩(데이터 미도착) 상태
    if (loading && !data) {
        return <Loading />;
    }
    if (error) {
        return <Error error={error} />;
    }
    // 모델이 존재하지 않으면(이상한 값 입력 등) API 가 null 을 반환 → 404
    if (!container || !model) {
        return <NotFound />;
    }

    // 모델은 존재하지만 노출 상품이 0건인 경우.
    if (!loading && items.length === 0 && sort === SORT_OPTIONS.POPULAR.code) {
        return <NotFound />;
    }

    return (
        <section className="ProductModel">
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />

                <meta property="og:site_name" content="FruitsFamily" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={canonicalUrl} />
                {items[0] && Array.isArray(items[0].resizedSmallImages) && items[0].resizedSmallImages[0] && (
                    <meta property="og:image" content={items[0].resizedSmallImages[0]} />
                )}

                <script type="application/ld+json">
                    {safeJsonLd({
                        ...SCHEMA_CONTEXT,
                        '@graph': [
                            {
                                '@type': 'CollectionPage',
                                name: metaTitle,
                                description: metaDescription,
                                url: canonicalUrl,
                                about: {
                                    '@type': 'Brand',
                                    name: brandName,
                                    url: webappURL(hostname) + brandUrl(brandName),
                                },
                                inLanguage: 'ko-KR',
                            },
                            breadcrumbSchemaOrg([
                                { name: '홈', url: webappURL(hostname) + '/' },
                                { name: brandName, url: webappURL(hostname) + brandUrl(brandName) },
                                { name: model.name_kr ? `${model.name}(${model.name_kr})` : model.name, url: canonicalUrl },
                            ]),
                        ],
                    })}
                </script>
            </Helmet>

            <ProductModelHero brandName={brandName} model={model} />

            <ProductModelResults
                items={items}
                total={total}
                loading={loading}
                fetchMore={fetchMore}
                sort={sort}
                onSortChange={handleSortChange}
                schemaOrgPreview={schemaOrgPreview}
            />
        </section>
    );
}

ProductModel.propTypes = {
    hostname: PropTypes.string.isRequired,
};
